import type { BrandSpecials } from '@iheartjane/data-access';
import { EXTRACT, FLOWERS } from '@iheartjane/menu';
import type { BrandSpecialPrices, PriceId } from '@iheartjane/types';

import type { ProductViewModel } from '@jane/bloom/viewModels/productDetail';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import type { AlgoliaProduct } from '@jane/search/types';

import {
  brandSpecialHasOneDiscount,
  getFirstBrandSpecial,
} from './brandSpecialDiscounts';

/**
 * This function returns a label for a brand discount
 * It can be passed either a menuProduct or a brandSpecials object, but not both.
 * If the menuProduct does not have a brand discount, then it will return null, and therefore should suffice as an equality check.
 * If you want to make sure a specific priceId exists within a brand special before getting its label,
 * use the getPriceIdBrandSpecial helper to return the whole special for that price id, if it has one.
 * If you want to extract a generic tag label for multiple priceIds, pass only the brand special.
 * It will return the discount amount if all the priceIds share a discount id,
 * otherwise it will return the fallback label.
 */

type BrandSpecialTagLabelKey = 'discount_label' | 'title';

export type GetJaneGoldTagLabelProps = {
  brandSpecials?: BrandSpecialPrices;
  fallbackLabel?: string;
  menuProduct?:
    | ProductViewModel
    | ProductSearchResultViewModel
    | AlgoliaProduct;
  priceId?: PriceId;
  priorityValueKey?: BrandSpecialTagLabelKey;
} & (
  | {
      brandSpecials: BrandSpecialPrices;
      menuProduct?: never;
    }
  | {
      brandSpecials?: never;
      menuProduct:
        | ProductViewModel
        | ProductSearchResultViewModel
        | AlgoliaProduct;
    }
);

const fallbackKeys: {
  [Property in BrandSpecialTagLabelKey]: BrandSpecialTagLabelKey;
} = {
  discount_label: 'title',
  title: 'discount_label',
};

export const getJaneGoldTagLabel = ({
  brandSpecials,
  fallbackLabel = 'Cash Back',
  menuProduct,
  priceId,
  priorityValueKey = 'discount_label',
}: GetJaneGoldTagLabelProps) => {
  if (!menuProduct && !brandSpecials) return null;

  let brandSpecialsPrices = brandSpecials;

  const brandSpecialPriceValues = Object.values(
    menuProduct?.brand_special_prices || {}
  ).filter(Boolean);

  if (menuProduct) {
    if (
      !menuProduct.brand_special_prices ||
      brandSpecialPriceValues.length === 0 ||
      !menuProduct.has_brand_discount
    )
      return null;

    brandSpecialsPrices = menuProduct.brand_special_prices;
  }

  if (!priceId)
    return getLabelForMultiplePriceIds(
      brandSpecialsPrices as BrandSpecialPrices,
      fallbackLabel,
      priorityValueKey
    );

  const priceIdSpecial = (brandSpecialsPrices as BrandSpecialPrices)[priceId];
  const fallbackKey = fallbackKeys[priorityValueKey];

  return (
    priceIdSpecial?.[priorityValueKey] ||
    priceIdSpecial?.[fallbackKey] ||
    fallbackLabel
  );
};

const getLabelForMultiplePriceIds = (
  brandSpecials: BrandSpecialPrices,
  fallbackLabel: string,
  priorityValueKey: BrandSpecialTagLabelKey
) => {
  const hasOneDiscountId = brandSpecialHasOneDiscount(brandSpecials);

  if (hasOneDiscountId) {
    const firstSpecial = getFirstBrandSpecial(brandSpecials);
    const fallbackKey = fallbackKeys[priorityValueKey];

    return (
      firstSpecial?.[priorityValueKey] ||
      firstSpecial?.[fallbackKey] ||
      fallbackLabel
    );
  }

  return fallbackLabel;
};

/**
 *
 * getApplicableGoldTagLabel
 *
 * This function determines the appropriate gold tag label to display for products with Jane Gold rewards. It analyzes brand specials and return the correct label based on special types, applicable weights, and business rules.
 *
 * The business rules are:
 *
 * When a single brand special applies:
 * - Is it a product special? Use the discount label
 * - Is it a bundle special? Use 'Cash back'
 *
 * When multiple brand specials apply:
 * - Are they multiple product specials? Use the best discount label
 * - Are they multiple bundle specials? Use 'Cash back'
 * - Is it a mix of product and bundle specials? Use the best product special discount label
 */

/**
 * @param {object} params - The parameters:
 * @param {BrandSpecials} params.brandSpecials - Array of brand specials to analyze
 * @param {number[]} params.applicableBrandSpecialIds - Optional array of brand special IDs that apply, sorted by best discount
 * @param {PriceId} params.priceId - Optional price ID to check against applicable weights
 * @returns {string | null} The appropriate gold tag label string, or null if no applicable specials found
 */
export const getApplicableGoldTagLabel = ({
  applicableBrandSpecialIds,
  brandSpecials,
  priceId,
}: {
  applicableBrandSpecialIds?: number[];
  brandSpecials: BrandSpecials;
  priceId?: PriceId;
}): string | null => {
  if (!applicableBrandSpecialIds) return null;
  const defaultLabel = 'Cash back';
  // maintain the sort order of the brand special ids as they are sorted by the lowest price/best discount
  const applicableBrandSpecials = applicableBrandSpecialIds
    ? (applicableBrandSpecialIds
        .map((id) => brandSpecials?.find((special) => special.id === id))
        .filter(Boolean) as BrandSpecials)
    : [];

  if (applicableBrandSpecials.length > 1) {
    // if more than one, check if a product special exists
    const productBrandSpecial = applicableBrandSpecials.find(
      ({ special_type }) => special_type === 'product'
    );

    if (productBrandSpecial) {
      // return the label for the best discount
      return productBrandSpecial.discount_label;
    } else {
      // or if they're bundle specials, return default
      return defaultLabel;
    }
  }

  if (applicableBrandSpecials.length === 1) {
    const brandSpecial = applicableBrandSpecials[0];

    if (priceId) {
      const applicableWeights = brandSpecial.applicable_weights;
      // if its an array, its a product special
      if (Array.isArray(applicableWeights)) {
        return applicableWeights.includes(priceId)
          ? brandSpecial.discount_label
          : null;
      }

      // if qualify/discount, its a bundle special
      return applicableWeights?.discount?.includes(priceId) ||
        applicableWeights?.qualify?.includes(priceId)
        ? defaultLabel
        : null;
    }

    return brandSpecial.special_type === 'product'
      ? brandSpecial.discount_label
      : defaultLabel;
  }

  return null;
};

function enabledWeightsForMenuProduct(
  menuProduct: ProductViewModel | AlgoliaProduct
) {
  const weights = ['flower', 'extract', 'vape'].includes(menuProduct.kind)
    ? menuProduct.kind === 'flower'
      ? FLOWERS
      : EXTRACT
    : [];

  return weights.reduce<
    {
      disabled: boolean;
      price?: number | string;
      value: PriceId;
    }[]
  >((accum, weight) => {
    const price =
      menuProduct[`discounted_price_${weight.id}`] ||
      menuProduct[`price_${weight.id}`];

    if (price) {
      accum.push({
        disabled: false,
        price,
        value: weight.id,
      });
    }

    return accum;
  }, []);
}

/**
 * Determines the appropriate top level gold tag label for Menu PDP (Product Detail Page).
 *
 * Provides similar functionality as `getApplicableGoldTagLabel`, but in the case of one applicable special, it considers if the special has weight rules. In this case, the function returns null because tags will be shown next to specific weights instead of under the product name.
 *
 * The business rules are:
 *
 * When a single brand special applies:
 * - Is it a product special? Use the discount label, but do not return a label if the special has weight rules
 * - Is it a bundle special? Use 'Cash back', but do not return a label if the special has weight rules
 *
 * When multiple brand specials apply:
 * - Are they multiple product specials? Use the best discount label
 * - Are they multiple bundle specials? Use 'Cash back'
 * - Is it a mix of product and bundle specials? Use the best product special discount label
 * @param {object} params - The parameters:
 * @param {BrandSpecials} params.brandSpecials - Array of brand specials to analyze (sorted by best special)
 * @param {ProductViewModel | AlgoliaProduct} params.menuProduct - The menu product to analyze
 * @param {PriceId} params.selectedWeight - The weight to check against applicable weights
 * @returns {string | null} The appropriate gold tag label string, or null if no applicable specials found
 */
export const getMenuPdpApplicableGoldTagLabel = ({
  brandSpecials,
  menuProduct,
  selectedWeight,
}: {
  brandSpecials: BrandSpecials;
  menuProduct: ProductViewModel | AlgoliaProduct;
  selectedWeight: PriceId;
}): string | null => {
  if (!brandSpecials) return null;

  const defaultLabel = 'Cash back';

  // if more than one, check if a product special exists
  if (brandSpecials?.length > 1) {
    const productBrandSpecial = brandSpecials.find(
      ({ special_type }) => special_type === 'product'
    );

    if (productBrandSpecial) {
      // return the label for the best discount
      return productBrandSpecial.discount_label;
    } else {
      // or if they're bundle specials, return default
      return defaultLabel;
    }
  }

  // if a single brand special applies, check if there is a weight rule
  if (brandSpecials?.length === 1) {
    const menuProductWeights = enabledWeightsForMenuProduct(menuProduct);

    const applicableWeights = brandSpecials[0].applicable_weights;

    // if its an array, it's a product special
    if (Array.isArray(applicableWeights)) {
      const hasWeightRule =
        menuProductWeights?.find(
          (weight) => !applicableWeights.includes(weight.value)
        ) || false;

      return hasWeightRule ? null : brandSpecials[0].discount_label;
    }
    // if qualify/discount, it's a bundle special
    const hasWeightRule =
      menuProductWeights?.find(
        (weight) =>
          !applicableWeights?.discount?.includes(weight.value) &&
          !applicableWeights?.qualify?.includes(weight.value)
      ) || false;
    const isSelectedWeightDiscounted =
      applicableWeights?.discount?.includes(selectedWeight) ||
      applicableWeights?.qualify?.includes(selectedWeight);

    return hasWeightRule && !isSelectedWeightDiscounted ? null : defaultLabel;
  }

  return null;
};
